import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { COLORS, useStyles } from '../../styles';
import { QeScoreMonthlyTrend } from '../../interfaces';
import { getBackgroundColorByScore, getTextColorByScore } from '../../utils';

const LEGEND_ITEMS = [
  { label: 'Excellent' },
  { label: 'Good' },
  { label: 'Fair' },
  { label: 'Poor' },
  { label: 'Very Poor' },
  { label: 'N/A' },
];

const formatMonthYearLabel = (value: string) => {
  const monthYearMatch = /^([A-Za-z]+)\s+(\d{4})$/.exec(value?.trim() ?? '');

  if (!monthYearMatch) {
    return value;
  }

  const [, month, year] = monthYearMatch;
  return `${month.slice(0, 3)} ${year}`;
};

const CustomBarLabel = (props: any) => {
  const { x, y, width, value } = props;
  const boxHeight = 36;
  const boxWidth = width;
  const labelOffset = 4; // distance between bar and label
  return (
    <g>
      <rect
        x={x}
        y={y - boxHeight - labelOffset}
        width={boxWidth}
        height={boxHeight}
        fill={getBackgroundColorByScore(value)}
        rx={4}
        ry={4}
      />
      <text
        x={x + boxWidth / 2}
        y={y - labelOffset - boxHeight / 2 + 4}
        textAnchor="middle"
        fill={getTextColorByScore(value)}
        fontSize={14}
        fontWeight={600}
      >
        {value}
      </text>
    </g>
  );
};

const CustomLegend = () => {
  const classes = useStyles();

  const legendClassByLabel = (label: string) => {
    if (label === 'Very Poor') return classes.qeTrendLegendVeryPoor;
    if (label === 'Poor') return classes.qeTrendLegendPoor;
    if (label === 'Fair') return classes.qeTrendLegendFair;
    if (label === 'Good') return classes.qeTrendLegendGood;
    if (label === 'Excellent') return classes.qeTrendLegendExcellent;
    return classes.qeTrendLegendNotApplicable;
  };

  return (
    <div className="flex items-center justify-end gap-3 sm:gap-4 md:gap-6 flex-wrap">
      {LEGEND_ITEMS.map(({ label }) => (
        <div key={label} className="flex items-center gap-1 flex-shrink-0">
          <div
            className={`rounded-sm flex-shrink-0 ${
              classes.qeTrendLegendBox
            } ${legendClassByLabel(label)}`}
          />
          <span className={`text-sm ${classes.qeTrendLegendLabel}`}>
            {label}
          </span>
        </div>
      ))}
    </div>
  );
};

export function QEScoreTrend({
  trendData,
}: {
  trendData: QeScoreMonthlyTrend[];
}) {
  const classes = useStyles();

  return (
    <div
      className={`rounded-lg shadow-sm border flex flex-col gap-2 flex-1 ${classes.qeTrendContainer}`}
    >
      {/* Title */}
      <div className={`text-xl font-semibold ${classes.qeTrendTitle}`}>
        QE Score Trend
      </div>

      {/* Legend */}
      <div className="flex justify-end overflow-x-auto">
        <CustomLegend />
      </div>

      {/* Chart */}
      <div className={`border rounded w-full ${classes.qeTrendChartContainer}`}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart
            data={trendData}
            margin={{ top: 40, right: 30, left: 0, bottom: 20 }}
          >
            <CartesianGrid
              strokeDasharray="0"
              stroke={COLORS.neutral.light}
              vertical={false}
            />
            <Bar
              dataKey="maxWeight"
              barSize={60}
              radius={[4, 4, 0, 0]}
              isAnimationActive={false}
              label={<CustomBarLabel />}
            >
              {trendData.map((entry, index) => (
                <Cell
                  key={`qe-trend-bar-${entry.month}-${index}`}
                  fill={'var(--qe-trend-bar-color)'}
                />
              ))}
            </Bar>
            <XAxis
              dataKey="month"
              tickFormatter={formatMonthYearLabel}
              tick={{ fontSize: 12, fill: COLORS.neutral.text }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 20, 40, 60, 80, 100]}
              tick={{ fontSize: 12, fill: COLORS.neutral.text }}
              axisLine={false}
              tickLine={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
